<script setup lang="ts">
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";
import WrDesignShineText from "~/components/molecules/WrDesignShineText.vue";
import WrDesignProcessCardsGrid from "~/components/molecules/WrDesignProcessCardsGrid.vue";
import WrDesignReadyToElevateYourBrand from "~/components/molecules/WrDesignReadyToElevateYourBrand.vue";
import WrProblemSolutionSection from "~/components/molecules/WrProblemSolutionSection.vue";
import WrDesignDualCardsSection from "~/components/molecules/WrDesignDualCardsSection.vue";
import WrDesignPipelineFlowSection from "~/components/molecules/WrDesignPipelineFlowSection.vue";
import WrDesignLevelUpYourCreativityWithAi from "~/components/molecules/WrDesignLevelUpYourCreativityWithAi.vue";
import WrDesignRealWorldApplicationsSection from "~/components/molecules/WrDesignRealWorldApplicationsSection.vue";
import WrDesignReasonsSection from "~/components/molecules/WrDesignReasonsSection.vue";
import WrDesignFourCardTransformGridSection from "~/components/molecules/WrDesignFourCardTransformGridSection.vue";
import WrDesignIconNumberHeadingCards from "~/components/molecules/WrDesignIconNumberHeadingCards.vue";
import WrDesignFaqsSection from "~/components/molecules/WrDesignFaqsSection.vue";
import WrDesignStatsCard from "~/components/molecules/WrDesignStatsCard.vue";
import WrTestimonialCardCarousel from "~/components/molecules/WrTestimonialCardCarousel.vue";
import WrSelectedProject from "~/components/molecules/WrSelectedProject.vue";
import WrIcon from "~/components/global/WrIcon.vue";

const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue")
);

/* ─── State & Composables ─── */
const visible = ref<boolean>(false);
const formPrefilledData = ref<any>(null);
const openModal = (prefilledValue: any = null): void => {
  if (prefilledValue == null) formPrefilledData.value = null;
  else if (typeof prefilledValue === 'string') formPrefilledData.value = prefilledValue;
  visible.value = true;
};

/* ─── Fetch Content ─── */
const { data } = await useFetch(
  `${useRequestURL().origin}/content/services/frameworks/adonisjs-development-services-content.json`
);
if (!data.value) throw createError({ statusCode: 404, statusMessage: "Page content not found" });
const content = data.value as any;

const {
  meta_tags,
  hero,
  shineText,
  quickHits,
  businessOutcomes,
  whyItMatters,
  capabilities,
  processMotion,
  techStack,
  integrationCards: integrationCardsData,
  useCases,
  whyChooseUs,
  cta,
  testimonials,
  faq,
  hire_us,
} = content;

/* ─── SEO Metadata & Schema ─── */
useHead({
  title: meta_tags.title,
  link: [{ rel: "canonical", href: meta_tags.canonical }],
  meta: [...meta_tags.meta, { name: "robots", content: "index, follow" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: meta_tags.serviceSchema.name,
        serviceType: meta_tags.serviceSchema.serviceType,
        url: meta_tags.canonical,
        provider: {
          "@type": "Organization",
          name: "WebReinvent",
          url: "https://webreinvent.com",
        },
        description: meta_tags.serviceSchema.description,
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://webreinvent.com" },
          { "@type": "ListItem", position: 2, name: "Services", item: "https://webreinvent.com/services" },
          { "@type": "ListItem", position: 3, name: meta_tags.serviceSchema.breadcrumbName, item: meta_tags.canonical },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.faqItems.map((item: { title: string; description: string }) => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: { "@type": "Answer", text: item.description },
        })),
      }),
    },
  ],
});

useSeoMeta({
  ogTitle: meta_tags.meta.find((m: any) => m.property === 'og:title')?.content || meta_tags.title,
  ogDescription: meta_tags.meta.find((m: any) => m.property === 'og:description')?.content,
  ogImage: meta_tags.meta.find((m: any) => m.property === 'og:image')?.content,
  ogUrl: meta_tags.canonical,
  ogType: 'website',
  twitterTitle: meta_tags.meta.find((m: any) => m.name === 'twitter:title')?.content,
  twitterDescription: meta_tags.meta.find((m: any) => m.name === 'twitter:description')?.content,
  twitterImage: meta_tags.meta.find((m: any) => m.name === 'twitter:image')?.content,
  twitterCard: 'summary_large_image',
  twitterSite: '@webreinvent',
  ogSiteName: 'WebReinvent',
});

definePageMeta({
  layout: "services-layout",
});

/* ─── Mapped Data ─── */

// ProcessCardsGrid: { title, description, svgIcon }
const quickHitCards = computed(() =>
  quickHits.items.map((item: any) => ({
    title: item.title,
    description: item.desc,
    svgIcon: item.svgIcon,
  }))
);

// ReadyToElevateYourBrand: { title, description, svgIcon }
const businessOutcomeCards = computed(() =>
  businessOutcomes.items.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// DualCardsSection: { title, description, svgIcon }
const capabilitiesGroupOne = computed(() =>
  capabilities.groupOneCards.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);
const capabilitiesGroupTwo = computed(() =>
  capabilities.groupTwoCards.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// LevelUpYourCreativity: { svgIcon, title, description }
const techStackCards = computed(() =>
  techStack.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// PipelineFlow: { number, title, description, svgIcon }
const pipelineSteps = computed(() =>
  processMotion.steps.map((step: any) => ({
    number: step.number,
    title: step.title,
    description: step.description,
    svgIcon: step.svgIcon,
  }))
);

// RealWorldApplications: { number, title, description }
const integrationCards = computed(() =>
  integrationCardsData.items.map((card: any, i: number) => ({
    number: `0${i + 1}`,
    title: card.title,
    description: card.description,
  }))
);

// ReasonsSection: { title, desc } + svgIcons[]
const useCaseCards = computed(() =>
  useCases.items.map((card: any) => ({
    title: card.title,
    desc: card.desc,
  }))
);
const useCaseIcons = computed(() =>
  useCases.items.map((card: any) => card.svgIcon)
);

// FourCardTransformGrid: { svgIcon, title, description }
const whyChooseUsCards = computed(() =>
  whyChooseUs.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// IconNumberHeadingCards: { number, title, desc, svgIcon }
const ctaCards = computed(() =>
  cta.cards.map((card: any, i: number) => ({
    number: `0${i + 1}`,
    svgIcon: card.svgIcon,
    title: card.title,
    desc: card.description,
  }))
);

const faqItems = computed(() => faq.faqItems);

// Testimonials: { quote, authorName, authorRole, authorImage? }
const testimonialItems = computed(() =>
  testimonials.items.map((item: any) => ({
    quote: item.quote,
    authorName: item.authorName,
    authorRole: item.authorRole,
    authorImage: item.authorImage,
  }))
);

// Clean hero alt text by stripping HTML tags (e.g. <br />)
const heroAlt = computed(() => String(hero.heading).replace(/<[^>]*>/g, '').trim());
</script>

<template>
  <main class="md:min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">

      <!-- Section 1: Hero Card -->
      <header class="pt-32">
        <WrDesignHeroCard
          :heading="hero.heading"
          :subcopy="hero.subcopy"
          :subcopy-class="hero.subcopyClass"
          :cta-text="hero.ctaText"
          :image-src="hero.imageSrc"
          :alt="heroAlt"
          @cta-clicked="openModal"
        />
      </header>

      <!-- Section 1.5: Shine Text -->
      <section class="my-8" aria-label="Core Value Statement">
        <WrDesignShineText :text="shineText" />
      </section>

      <!-- Section 2: Quick Hits (ProcessCardsGrid - 3-col grid with icons) -->
      <section aria-label="Framework Capabilities at a Glance">
        <WrDesignProcessCardsGrid
          :heading="quickHits.heading"
          :subcopy="quickHits.subcopy"
          :cards="quickHitCards"
          :cta-text="quickHits.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 3: Business Outcomes (ReadyToElevateYourBrand - horizontal row cards with icons) -->
      <section aria-label="Business Outcomes">
        <WrDesignReadyToElevateYourBrand
          :heading="businessOutcomes.heading"
          :subcopy="businessOutcomes.subcopy"
          :cards="businessOutcomeCards"
          :cta-text="businessOutcomes.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 4: Problem / Solution (ProblemSolutionSection - 4 items each) -->
      <section aria-labelledby="why-it-matters-heading">
        <WrProblemSolutionSection
          id="why-it-matters-heading"
          :heading="whyItMatters.heading"
          :intro="whyItMatters.subcopy"
          :problem-label="whyItMatters.lossLabel"
          :problems="whyItMatters.losses"
          :solution-label="whyItMatters.deliverLabel"
          :solutions="whyItMatters.delivers"
          :quote="whyItMatters.quote"
          :cta-text="whyItMatters.ctaText"
          :bg-image-light="whyItMatters.bgImageLight"
          :bg-image-dark="whyItMatters.bgImageDark"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 5: Capabilities (DualCardsSection - two groups of cards) -->
      <section aria-label="Framework Capabilities">
        <WrDesignDualCardsSection
          :heading="capabilities.heading"
          :subtitle="capabilities.subcopy"
          :group-one-title="capabilities.groupOneTitle"
          :group-one-cards="capabilitiesGroupOne"
          :group-two-title="capabilities.groupTwoTitle"
          :group-two-cards="capabilitiesGroupTwo"
          :cta-text="capabilities.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 6: How Modern Frameworks Work (PipelineFlow - graphical pipeline) -->
      <section aria-label="How Modern Frameworks Work">
        <WrDesignPipelineFlowSection
          :heading="processMotion.heading"
          :steps="pipelineSteps"
        />
      </section>

      <!-- Section 7: Tech Stack (LevelUpYourCreativityWithAi - 4-col cards with icons) -->
      <section aria-label="Enterprise Architecture">
        <WrDesignLevelUpYourCreativityWithAi
          :heading="techStack.heading"
          :subcopy="techStack.subcopy"
          :cards="techStackCards"
          :cta-text="techStack.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 9: Integration Cards (RealWorldApplicationsSection - 4-column numbered grid) -->
      <section aria-label="Integrations and Systems">
        <WrDesignRealWorldApplicationsSection
          :single-line-heading="integrationCardsData.heading"
          :subtitle="integrationCardsData.subcopy"
          :cards="integrationCards"
          :cta-text="integrationCardsData.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 10: Real-World Use Cases (ReasonsSection - 3+2 layout with dividers) -->
      <section aria-label="Real-World Use Cases">
        <WrDesignReasonsSection
          :heading="useCases.heading"
          :subcopy="useCases.subcopy"
          :cards="useCaseCards"
          :svg-icons="useCaseIcons"
          :cta-text="useCases.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 11: Why Choose Us (FourCardTransformGridSection - 2x2 grid with crosshairs) -->
      <section aria-label="Why Choose WebReinvent">
        <WrDesignFourCardTransformGridSection
          :heading="whyChooseUs.heading"
          :subtitle="whyChooseUs.subcopy"
          :cards="whyChooseUsCards"
          :cta-text="whyChooseUs.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 12: CTA Block (IconNumberHeadingCards - numbered cards with dividers) -->
      <section aria-label="Call to Action">
        <WrDesignIconNumberHeadingCards
          :heading="cta.heading"
          :subcopy="cta.subcopy"
          :cards="ctaCards"
          :cta-text="cta.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in ctaCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-8 max-sm:size-6 text-[#501D71] dark:text-white" />
          </template>
        </WrDesignIconNumberHeadingCards>
      </section>

      <!-- Section 13: Testimonials -->
            <section class="relative mt-16 overflow-hidden max-w-screen-xl mx-auto px-4 z-20">
        <h2 class="text-center mb-11">
          <WrGradientText text="Trusted for excellence and efficiency" />
          <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
            WebReinvent is trusted by leading organisations to enhance their
            development and operational efficiency.
          </p>
        </h2>
        <WrTestimonialCardCarousel />
        <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7">
          View All
        </WrButton>
      </section>

      <!-- Section 14: FAQ (Filtered accordion with tags) -->
      <section aria-label="Frequently asked questions">
        <WrDesignFaqsSection
          :heading="faq.heading"
          :suffix="faq.suffix"
          :subtext="faq.subtext"
          :filter-tags="faq.filterTags"
          :faq-items="faqItems"
          :cta-text="faq.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 15: Products We Built -->
      <section aria-label="Products We Built" class="section-spacing dark:bg-none relative z-10">
        <div class="container-wrapper-lg w-full">
          <WrSelectedProject />
        </div>
      </section>

      <!-- Section 17: Stats Card -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Company Statistics">
        <WrDesignStatsCard />
      </section>

    </div>
  </main>

  <WrContactFormPopup :isOpen="visible" class="z-30" @close="visible = false" :prefilledContent="formPrefilledData" />
</template>

<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 400px;
}
</style>
